import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
  type DocumentSnapshot,
  type QuerySnapshot,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { DEBUG_TEAM_WORKSPACE_ID, getDebugWorkspaceActivity, isNoSignInPreviewActive } from '../debug'
import { firebaseAuth, firestoreDb, firebaseFunctions } from './firebase'
import { queryWorkspaceActivityPage } from './activityService'
import type { AiContextDigest, AiExecutionGraph, AiExecutionPlan, AiOperationEnvelope, AiOperationSimulation, AiProposalShareState, AiProposalShareVisibility, AiRollbackPlan } from '../types/aiOperational'

import { AI_AGENT_COSTS, AI_SURFACE_LABELS, type AiSurfaceKey } from './aiAgentCosts'
export { AI_AGENT_COSTS, AI_SURFACE_LABELS, type AiSurfaceKey }

export type RephraseReportInput = {
  workspaceId: string
  title?: string
  summary?: string
  contentBlocks?: Array<{ type?: string; text?: string; heading?: string }>
}

export type RephraseReportResult = {
  workspaceId: string
  creditsCharged: number
  rephrased: string
}

export const rephraseDeterministicReport = async (input: RephraseReportInput): Promise<RephraseReportResult> => {
  if (isNoSignInPreviewActive()) {
    return {
      workspaceId: input.workspaceId,
      creditsCharged: AI_AGENT_COSTS.REPORT_REPHRASE,
      rephrased: `${input.title ?? 'Workspace report'}: ${input.summary ?? 'no summary available in debug mode.'}`,
    }
  }
  await firebaseAuth.currentUser?.getIdToken()
  const call = httpsCallable<RephraseReportInput, RephraseReportResult>(firebaseFunctions, 'rephraseDeterministicReport')
  return (await call(input)).data
}

export type RephraseDigestInput = {
  workspaceId: string
  digestHeadline?: string
  events: Array<{ action?: string; summary?: string; targetTitle?: string }>
}

export type RephraseDigestResult = {
  workspaceId: string
  creditsCharged: number
  rephrased: string
}

export const rephraseWorkspaceDigest = async (input: RephraseDigestInput): Promise<RephraseDigestResult> => {
  if (isNoSignInPreviewActive()) {
    return {
      workspaceId: input.workspaceId,
      creditsCharged: AI_AGENT_COSTS.DIGEST_REPHRASE,
      rephrased: `${input.digestHeadline ?? 'Digest'}\n${input.events.slice(0, 3).map((event) => `• ${event.targetTitle ?? event.summary ?? event.action ?? 'Activity'}`).join('\n')}`,
    }
  }
  await firebaseAuth.currentUser?.getIdToken()
  const call = httpsCallable<RephraseDigestInput, RephraseDigestResult>(firebaseFunctions, 'rephraseWorkspaceDigest')
  return (await call(input)).data
}

export type PulseHealthState = 'HEALTHY' | 'WATCH' | 'CRITICAL'

export interface WorkspacePulseSnapshot {
  workspaceId: string
  healthState: PulseHealthState
  riskScore: number
  openRiskCount: number
  failedAutomationCount: number
  stockShortageCount: number
  walletCredits: number
  budget: {
    totalBudgetLimit: number
    totalActualSpend: number
    utilization: number
    overBudgetCount: number
  }
  activity: {
    last24hCount: number
    latest: Array<{ id: string; action: string; summary: string; targetId: string }>
  }
  topRisks: Array<{ id: string; targetId: string; summary: string; source: string }>
}

export interface WorkspacePulseEvent {
  id: string
  workspaceId: string
  type: 'MEETING_READINESS_ALERT' | 'AI_RESOLUTION_PROPOSAL' | string
  severity: 'INFO' | 'WATCH' | 'CRITICAL' | string
  title: string
  summary: string
  targetId: string
  targetType: string
  createdAt: unknown
}

export interface PulseSummaryResult {
  reportId: string
  cached: boolean
  creditsCharged: number
  summary: string
}

export interface AiProposalExecutionResult {
  proposalId: string
  status: 'EXECUTED'
  creditsCharged: number
  result: unknown
}

export interface AiTreeProposalResult {
  proposalId: string
  status: AiProposalStatus
  creditCost: number
}

export type ChatProposalResult =
  | { status: 'CLARIFICATION'; clarificationMessage: string }
  | { status: AiProposalStatus; proposalId: string; creditCost: number }

export type AiProposalStatus = 'PENDING' | 'PENDING_REVIEW' | 'EXECUTING' | 'EXECUTED' | 'FAILED' | 'REJECTED'
export type AiProposalType = 'TREE_CREATE' | 'STATUS_DELTA' | 'RESOLUTION_TREE'

export interface AiPlanProposal {
  id: string
  workspaceId: string
  createdBy: string
  status: AiProposalStatus
  proposalType: AiProposalType
  title: string
  creditCost: number
  createdAt: unknown
  updatedAt: unknown
  source?: Record<string, unknown> | null
  tree?: unknown
  delta?: unknown
  operationEnvelope?: AiOperationEnvelope | null
  simulation?: AiOperationSimulation | null
  rollback?: AiRollbackPlan | null
  contextDigest?: AiContextDigest | null
  executionPlan?: AiExecutionPlan | null
  executionGraph?: AiExecutionGraph | null
  shareState?: AiProposalShareState | null
}

const asNumber = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const asString = (value: unknown, fallback = '') => typeof value === 'string' && value.trim() ? value.trim() : fallback
const asRecord = (value: unknown) => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
const timestampMs = (value: unknown) => value instanceof Timestamp ? value.toMillis() : 0
const emptyQuerySnapshot = { docs: [] } as unknown as QuerySnapshot
const emptyDocSnapshot = { data: () => undefined } as unknown as DocumentSnapshot

const isPermissionDenied = (error: unknown) =>
  typeof error === 'object'
  && error !== null
  && 'code' in error
  && String((error as { code?: unknown }).code) === 'permission-denied'

const isAuthUnavailable = (error: unknown) =>
  typeof error === 'object'
  && error !== null
  && 'code' in error
  && /unauthenticated|permission-denied|failed-precondition|not-found/i.test(String((error as { code?: unknown }).code))

const optionalPulseRead = async <T>(read: Promise<T>, fallback: T): Promise<T> => {
  try {
    return await read
  } catch (error) {
    if (!isPermissionDenied(error) && !isAuthUnavailable(error)) {
      console.warn('[workspace-pulse] optional source failed', error)
    }
    return fallback
  }
}

const debugPulse = (workspaceId: string): WorkspacePulseSnapshot => {
  const activity = getDebugWorkspaceActivity(DEBUG_TEAM_WORKSPACE_ID)
  const latest = activity.slice(0, 4).map((entry) => ({
    id: entry.id,
    action: entry.action,
    summary: entry.summary,
    targetId: entry.targetId,
  }))
  return {
    workspaceId,
    healthState: 'WATCH',
    riskScore: 48,
    openRiskCount: 2,
    failedAutomationCount: 1,
    stockShortageCount: 1,
    walletCredits: 5000,
    budget: {
      totalBudgetLimit: 250000,
      totalActualSpend: 173000,
      utilization: 0.692,
      overBudgetCount: 1,
    },
    activity: {
      last24hCount: Math.max(8, activity.length),
      latest: latest.length ? latest : [
        { id: 'debug-pulse-activity-1', action: 'PROOF_VERIFIED', summary: 'Proof verified for launch checklist.', targetId: 'debug-task-finalize-checklist' },
        { id: 'debug-pulse-activity-2', action: 'RESOURCE_GATING_ERROR', summary: 'Stock shortage blocked a completion attempt.', targetId: 'debug-task-call-vendor' },
        { id: 'debug-pulse-activity-3', action: 'AI_CREDITS_COMMITTED', summary: 'AI credits committed for an approved proposal.', targetId: workspaceId },
      ],
    },
    topRisks: [
      { id: 'debug-risk-resource', targetId: 'debug-task-call-vendor', summary: 'Stock reservation is at capacity for vendor call follow-up.', source: 'RESOURCE_GATING_ERROR' },
      { id: 'debug-risk-automation', targetId: 'debug-automation-webhook', summary: 'Daily webhook digest failed on the last run.', source: 'AUTOMATION_FAILED' },
    ],
  }
}

export const subscribeWorkspacePulseEvents = (
  workspaceId: string,
  onChange: (events: WorkspacePulseEvent[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    onChange([
      {
        id: 'debug-pulse-event',
        workspaceId,
        type: 'MEETING_READINESS_ALERT',
        severity: 'WATCH',
        title: 'Meeting readiness below 80%',
        summary: 'Debug planning sync has 2 agenda blockers.',
        targetId: 'debug-meeting',
        targetType: 'MEETING',
        createdAt: Timestamp.now(),
      },
    ])
    return () => undefined
  }
  return onSnapshot(
    query(collection(firestoreDb, 'workspaces', workspaceId, 'workspace_pulse_events'), orderBy('createdAt', 'desc'), limit(20)),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as WorkspacePulseEvent)),
    (error) => onError?.(error),
  )
}

export const getWorkspacePulse = async (workspaceId: string): Promise<WorkspacePulseSnapshot> => {
  if (isNoSignInPreviewActive()) return debugPulse(workspaceId)
  await firebaseAuth.currentUser?.getIdToken()
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000)
  const now = new Date()
  const [risks, activity, wallet, budget, stock, automationRuns] = await Promise.all([
    optionalPulseRead(getDocs(query(collection(firestoreDb, 'project_risk_events'), where('workspaceId', '==', workspaceId), where('status', '==', 'OPEN'), limit(25))), emptyQuerySnapshot),
    optionalPulseRead(queryWorkspaceActivityPage(firestoreDb, workspaceId, { from: since, to: now, memberIds: [], itemIds: [], activityTypes: [] }, null, 100), { records: [], nextCursor: null, hasMore: false }),
    optionalPulseRead(getDoc(doc(firestoreDb, 'workspace_wallet', workspaceId)), emptyDocSnapshot),
    optionalPulseRead(getDoc(doc(firestoreDb, 'workspace_budget_summary', workspaceId)), emptyDocSnapshot),
    optionalPulseRead(getDocs(query(collection(firestoreDb, 'workspace_stock'), where('workspaceId', '==', workspaceId), limit(100))), emptyQuerySnapshot),
    optionalPulseRead(getDocs(query(collection(firestoreDb, 'automation_runs'), where('workspaceId', '==', workspaceId), limit(50))), emptyQuerySnapshot),
  ])
  const openRisks = risks.docs.map((entry) => ({ id: entry.id, ...(entry.data() as Record<string, unknown>) }))
  const recentActivity = activity.records
  const failedAutomations = automationRuns.docs.filter((entry) => String(entry.data().status) === 'FAILED').length
  const budgetData = budget.data() ?? {}
  const stockShortages = stock.docs.filter((entry) => asNumber(entry.data().quantity) <= asNumber(entry.data().reservedQuantity)).length
  const totalBudgetLimit = asNumber(budgetData.totalBudgetLimit)
  const totalActualSpend = asNumber(budgetData.totalActualSpend)
  const utilization = totalBudgetLimit > 0 ? totalActualSpend / totalBudgetLimit : 0
  const riskScore = Math.min(100, openRisks.length * 18 + failedAutomations * 12 + stockShortages * 10 + (utilization >= 1 ? 30 : utilization >= 0.8 ? 16 : 0))
  const healthState: PulseHealthState = riskScore >= 70 ? 'CRITICAL' : riskScore >= 35 ? 'WATCH' : 'HEALTHY'
  return {
    workspaceId,
    healthState,
    riskScore,
    openRiskCount: openRisks.length,
    failedAutomationCount: failedAutomations,
    stockShortageCount: stockShortages,
    walletCredits: asNumber(wallet.data()?.availableCredits),
    budget: {
      totalBudgetLimit,
      totalActualSpend,
      utilization,
      overBudgetCount: asNumber(budgetData.overBudgetCount),
    },
    activity: {
      last24hCount: recentActivity.length,
      latest: recentActivity
        .sort((a, b) => timestampMs(b.createdAt) - timestampMs(a.createdAt))
        .slice(0, 5)
        .map((entry) => ({
          id: String(entry.id),
          action: asString(entry.action),
          summary: asString(entry.summary, asString(entry.targetTitle, 'Workspace activity')),
          targetId: asString(entry.targetId),
        })),
    },
    topRisks: openRisks.slice(0, 5).map((risk) => {
      const data = risk as Record<string, unknown>
      return {
        id: String(data.id),
        targetId: asString(data.targetId),
        summary: asString(data.summary, asString(data.title, 'Open project risk')),
        source: asString(data.sourceAction, asString(data.action, 'RISK')),
      }
    }),
  }
}

export type WorkspaceRiskScanResult = {
  workspaceId: string
  creditsCharged: number
  riskScore: number
  healthState: PulseHealthState
  openRiskCount: number
  failedAutomationCount: number
  stockShortageCount: number
  budgetUtilization: number
  topRisks: Array<{ id: string; targetId: string; summary: string; source: string }>
}

export const scanWorkspaceRisk = async (workspaceId: string): Promise<WorkspaceRiskScanResult> => {
  if (isNoSignInPreviewActive()) {
    const pulse = debugPulse(workspaceId)
    return {
      workspaceId,
      creditsCharged: AI_AGENT_COSTS.RISK_SCANNER,
      riskScore: pulse.riskScore,
      healthState: pulse.healthState,
      openRiskCount: pulse.openRiskCount,
      failedAutomationCount: pulse.failedAutomationCount,
      stockShortageCount: pulse.stockShortageCount,
      budgetUtilization: pulse.budget.utilization,
      topRisks: pulse.topRisks,
    }
  }
  await firebaseAuth.currentUser?.getIdToken()
  const call = httpsCallable<{ workspaceId: string }, WorkspaceRiskScanResult>(firebaseFunctions, 'scanWorkspaceRisk')
  return (await call({ workspaceId })).data
}

export type WorkspaceBudgetAdvisorResult = {
  workspaceId: string
  creditsCharged: number
  budgetUtilization: number
  overBudgetCount: number
  stockShortageCount: number
  advisories: string[]
}

export const scanWorkspaceBudgetAdvisor = async (workspaceId: string): Promise<WorkspaceBudgetAdvisorResult> => {
  if (isNoSignInPreviewActive()) {
    const pulse = debugPulse(workspaceId)
    return {
      workspaceId,
      creditsCharged: AI_AGENT_COSTS.BUDGET_ADVISOR,
      budgetUtilization: pulse.budget.utilization,
      overBudgetCount: pulse.budget.overBudgetCount,
      stockShortageCount: pulse.stockShortageCount,
      advisories: ['Debug workspace has no live budget/stock data — advisories will populate against a real workspace.'],
    }
  }
  await firebaseAuth.currentUser?.getIdToken()
  const call = httpsCallable<{ workspaceId: string }, WorkspaceBudgetAdvisorResult>(firebaseFunctions, 'scanWorkspaceBudgetAdvisor')
  return (await call({ workspaceId })).data
}

export const generatePulseSummary = async (workspaceId: string, forceRefresh = false): Promise<PulseSummaryResult> => {
  if (isNoSignInPreviewActive()) {
    const pulse = debugPulse(workspaceId)
    return {
      reportId: `debug-ai-pulse-${workspaceId}`,
      cached: false,
      creditsCharged: AI_AGENT_COSTS.PULSE_SUMMARY,
      summary: `Workspace Pulse: ${pulse.healthState.toLowerCase()} (${pulse.riskScore}/100). ${pulse.openRiskCount} open risks and ${pulse.activity.last24hCount} recent activity events need attention.`,
    }
  }
  await firebaseAuth.currentUser?.getIdToken()
  const call = httpsCallable<{ workspaceId: string; forceRefresh?: boolean }, PulseSummaryResult>(firebaseFunctions, 'generatePulseSummary')
  return (await call({ workspaceId, forceRefresh })).data
}

export const executeAIProposal = async (
  proposalId: string,
  options: { humanConfirmed?: boolean } = {},
): Promise<AiProposalExecutionResult> => {
  if (options.humanConfirmed !== true) {
    throw new Error('Explicit human confirmation is required before executing an AI proposal.')
  }
  if (isNoSignInPreviewActive()) {
    return {
      proposalId,
      status: 'EXECUTED',
      creditsCharged: AI_AGENT_COSTS.BIG_IDEA,
      result: {
        drafts: [
          { id: `debug-draft-${proposalId}`, kind: 'GOAL', parentId: null },
        ],
      },
    }
  }
  const call = httpsCallable<{ proposalId: string; humanConfirmed?: boolean }, AiProposalExecutionResult>(firebaseFunctions, 'executeAIProposal')
  return (await call({ proposalId, humanConfirmed: options.humanConfirmed === true })).data
}

export const dismissAIProposal = async (proposalId: string): Promise<{ proposalId: string; status: 'REJECTED' }> => {
  if (isNoSignInPreviewActive()) return { proposalId, status: 'REJECTED' }
  const call = httpsCallable<{ proposalId: string }, { proposalId: string; status: 'REJECTED' }>(firebaseFunctions, 'dismissAIProposal')
  return (await call({ proposalId })).data
}

export const updateAIProposalShareState = async (input: {
  proposalId: string
  visibility: AiProposalShareVisibility
  reason?: string
}): Promise<{ proposalId: string; shareState: AiProposalShareState }> => {
  if (isNoSignInPreviewActive()) {
    return {
      proposalId: input.proposalId,
      shareState: {
        visibility: input.visibility,
        ownerId: firebaseAuth.currentUser?.uid ?? 'debug-user',
        reviewerRoles: input.visibility === 'PRIVATE' ? [] : ['ADMIN', 'EDITOR'],
        sharedBy: firebaseAuth.currentUser?.uid ?? 'debug-user',
        sharedAtIso: new Date().toISOString(),
        ...(input.reason ? { reason: input.reason } : {}),
      },
    }
  }
  const call = httpsCallable<
    { proposalId: string; visibility: AiProposalShareVisibility; reason?: string },
    { proposalId: string; shareState: AiProposalShareState }
  >(firebaseFunctions, 'updateAIProposalShareState')
  return (await call(input)).data
}

export const createAITreeProposal = async (input: {
  workspaceId: string
  title: string
  tree: unknown
  board: string
  creditCost?: number
  operationEnvelope?: AiOperationEnvelope
  simulation?: AiOperationSimulation
  rollback?: AiRollbackPlan
  contextDigest?: AiContextDigest
  executionPlan?: AiExecutionPlan
  executionGraph?: AiExecutionGraph
  shareState?: AiProposalShareState
}): Promise<AiTreeProposalResult> => {
  if (isNoSignInPreviewActive()) {
    return {
      proposalId: `debug-tree-proposal-${Date.now()}`,
      status: 'PENDING',
      creditCost: input.creditCost ?? AI_AGENT_COSTS.BIG_IDEA,
    }
  }
  const call = httpsCallable<Record<string, unknown>, AiTreeProposalResult>(firebaseFunctions, 'createAIProposal')
  return (await call({
    workspaceId: input.workspaceId,
    proposalType: 'TREE_CREATE',
    title: input.title,
    tree: input.tree,
    board: input.board,
    creditCost: input.creditCost ?? AI_AGENT_COSTS.BIG_IDEA,
    ...(input.operationEnvelope ? { operationEnvelope: input.operationEnvelope } : {}),
    ...(input.simulation ? { simulation: input.simulation } : {}),
    ...(input.rollback ? { rollback: input.rollback } : {}),
    ...(input.contextDigest ? { contextDigest: input.contextDigest } : {}),
    ...(input.executionPlan ? { executionPlan: input.executionPlan } : {}),
    ...(input.executionGraph ? { executionGraph: input.executionGraph } : {}),
    ...(input.shareState ? { shareState: input.shareState } : {}),
  })).data
}

export const createChatProposal = async (input: {
  workspaceId: string
  channelId: string
  text: string
}): Promise<ChatProposalResult> => {
  if (isNoSignInPreviewActive()) {
    return { status: 'PENDING', proposalId: `debug-chat-proposal-${Date.now()}`, creditCost: AI_AGENT_COSTS.BIG_IDEA }
  }
  const call = httpsCallable<Record<string, unknown>, ChatProposalResult>(firebaseFunctions, 'createChatProposal')
  return (await call(input)).data
}

const normalizeProposalStatus = (value: unknown): AiProposalStatus =>
  value === 'PENDING_REVIEW' || value === 'EXECUTING' || value === 'EXECUTED' || value === 'FAILED' || value === 'REJECTED'
    ? value
    : 'PENDING'

const normalizeProposalType = (value: unknown): AiProposalType =>
  value === 'STATUS_DELTA' ? 'STATUS_DELTA' : value === 'RESOLUTION_TREE' ? 'RESOLUTION_TREE' : 'TREE_CREATE'

const normalizePlanProposal = (id: string, data: Record<string, unknown>): AiPlanProposal => ({
  id,
  workspaceId: asString(data.workspaceId),
  createdBy: asString(data.createdBy),
  status: normalizeProposalStatus(data.status),
  proposalType: normalizeProposalType(data.proposalType),
  title: asString(data.title, 'AI plan proposal'),
  creditCost: Math.max(0, Math.ceil(asNumber(data.creditCost))),
  createdAt: data.createdAt ?? null,
  updatedAt: data.updatedAt ?? null,
  source: data.source && typeof data.source === 'object' && !Array.isArray(data.source) ? data.source as Record<string, unknown> : null,
  tree: data.tree,
  delta: data.delta,
  operationEnvelope: asRecord(data.operationEnvelope) as AiOperationEnvelope | null,
  simulation: asRecord(data.simulation) as AiOperationSimulation | null,
  rollback: asRecord(data.rollback) as AiRollbackPlan | null,
  contextDigest: asRecord(data.contextDigest) as AiContextDigest | null,
  executionPlan: asRecord(data.executionPlan) as AiExecutionPlan | null,
  executionGraph: asRecord(data.executionGraph) as AiExecutionGraph | null,
  shareState: asRecord(data.shareState) as AiProposalShareState | null,
})

export const subscribePlanProposals = (input: {
  workspaceId: string | null
  uid: string | null
  canApprove: boolean
  onChange: (proposals: AiPlanProposal[]) => void
  onError?: (error: Error) => void
}): Unsubscribe => {
  if (!input.workspaceId || !input.uid) {
    input.onChange([])
    return () => undefined
  }
  if (isNoSignInPreviewActive()) {
    input.onChange([
      {
        id: 'debug-ai-plan-proposal',
        workspaceId: input.workspaceId,
        createdBy: input.uid,
        status: 'PENDING',
        proposalType: 'TREE_CREATE',
        title: 'Debug launch plan proposal',
        creditCost: AI_AGENT_COSTS.BIG_IDEA,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        source: { kind: 'DEBUG' },
        operationEnvelope: {
          version: 1,
          workspaceId: input.workspaceId,
          createdAtIso: new Date().toISOString(),
          sourceSurface: 'AI_MANAGER',
          risk: 'HIGH',
          reviewRequired: true,
          operations: [{
            id: 'debug-operation-tree-create',
            kind: 'TIMELINE_CREATE_DRAFT',
            title: 'Create debug launch plan',
            targetRefs: [{ id: input.workspaceId, type: 'WORKSPACE', title: 'Debug workspace' }],
            risk: 'HIGH',
            requiresReview: true,
            requiresConfirmation: false,
            allowedAutoExecute: false,
            rollback: { strategy: 'AUDIT_REPLAY', reversible: true, notes: ['Debug proposal rollback metadata.'] },
          }],
          audit: {
            summary: 'Debug proposal uses operational metadata for review UI.',
            rationale: ['Local debug path only.'],
            sourceSurface: 'AI_MANAGER',
            actor: { uid: input.uid },
            workspaceId: input.workspaceId,
          },
          simulation: {
            summary: 'Approval would create a draft launch plan through the gated mutation path.',
            affectedRecords: [{ id: input.workspaceId, type: 'WORKSPACE', title: 'Debug workspace' }],
            risks: [{ level: 'HIGH', message: 'Draft tree creation requires review.' }],
          },
        },
        rollback: { strategy: 'AUDIT_REPLAY', reversible: true, notes: ['Debug proposal rollback metadata.'] },
        tree: {
          title: 'Debug launch plan',
          tasks: [
            { title: 'Confirm proposal approval path' },
            { title: 'Stage draft review in Manage' },
          ],
        },
      },
    ])
    return () => undefined
  }
  const constraints = input.canApprove
    ? [where('workspaceId', '==', input.workspaceId)]
    : [where('createdBy', '==', input.uid)]
  return onSnapshot(
    query(collection(firestoreDb, 'ai_proposals'), ...constraints),
    (snapshot) => {
      const proposals = snapshot.docs
        .map((entry) => normalizePlanProposal(entry.id, entry.data() as Record<string, unknown>))
        .filter((proposal) => proposal.workspaceId === input.workspaceId)
        .filter((proposal) => proposal.status === 'PENDING' || proposal.status === 'PENDING_REVIEW')
        .sort((left, right) => timestampMs(right.createdAt) - timestampMs(left.createdAt))
      input.onChange(proposals)
    },
    (error) => input.onError?.(error),
  )
}
